import { LOCALE_PATTERN, normalizeLocale } from '../i18n.js'
import { invalidTimezoneMessage, isValidTimezone } from '../utils/timezone.js'
import { DIET_TAGS, MAX_FLAVOR_TAGS } from '../attributes.js'
import { z } from 'zod'

export * from './promotions.js'
export * from './daily-specials.js'

// ─── Paginação ──────────────────────────────────────────────────────────────────

export const PaginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
})

// ─── Auth ───────────────────────────────────────────────────────────────────────

/**
 * E-mail normalizado (trim + minúsculas) — forma canônica em `users.email`,
 * `otp_codes.contact`, chaves de rate limit e `trial_history.owner_email`
 * (#313 QA: `QA+X@` e `qa+x@` eram contas/limites distintos).
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

export const EmailSchema = z.string().trim().toLowerCase().email()

export const OtpRequestSchema = z
  .object({
    email: EmailSchema.optional(),
    phone: z.string().min(7).max(20).optional(),
  })
  .refine((v) => v.email !== undefined || v.phone !== undefined, {
    message: 'Pelo menos um de: email ou phone',
  })

export const OtpVerifySchema = z.object({
  // e-mail → forma canônica (igual ao /otp/request); telefone só sem espaços
  contact: z
    .string()
    .trim()
    .min(5)
    .transform((v) => (v.includes('@') ? normalizeEmail(v) : v)),
  code: z.string().length(6).regex(/^\d+$/, 'OTP deve ter 6 dígitos'),
})

// ─── Tenant ─────────────────────────────────────────────────────────────────────

export const TenantSlugSchema = z
  .string()
  .min(3)
  .max(48)
  .regex(/^[a-z0-9-]+$/, 'Slug deve conter apenas letras minúsculas, números e hífens')

// ─── Pedido ─────────────────────────────────────────────────────────────────────
// Schema canônico de criação de pedido — usado pela API (rota /orders) e
// disponível aos frontends para validação client-side.

export const OrderItemSchema = z.object({
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable().optional(),
  quantity: z.number().int().min(1).max(20),
  notes: z.string().max(300).nullable().optional(),
  removed_ingredient_ids: z.array(z.string().uuid()).max(20).default([]),
  extra_ids: z
    .array(
      z.object({
        extra_id: z.string().uuid(),
        quantity: z.number().int().min(1).max(10).default(1),
      }),
    )
    .max(20)
    .default([]),
})

export const OrderCreateSchema = z.object({
  channel: z.enum(['dine_in', 'takeaway', 'delivery']).default('dine_in'),
  table_id: z.string().uuid().nullable().optional(),
  reference_name: z.string().max(64).nullable().optional(),
  delivery_address: z
    .object({
      street: z.string(),
      city: z.string(),
      postal_code: z.string(),
      country: z.string().length(2),
    })
    .optional(),
  notes: z.string().max(500).nullable().optional(),
  fiscal_id: z.string().max(32).nullable().optional(),
  tip_cents: z.number().int().min(0).default(0),
  items: z.array(OrderItemSchema).min(1).max(50),
})

/**
 * Cotação (checkout): igual ao pedido, mas cada item pode trazer o preço
 * unitário que o cliente está VENDO (`expected_unit_price_cents`). O servidor
 * nunca confia nele para cobrar: só o compara com o preço calculado e devolve
 * `warnings` (ex.: a promoção terminou entre o carrinho e o envio) — #204.
 */
export const OrderQuoteItemSchema = OrderItemSchema.extend({
  expected_unit_price_cents: z.number().int().min(0).optional(),
})
export const OrderQuoteSchema = OrderCreateSchema.extend({
  items: z.array(OrderQuoteItemSchema).min(1).max(50),
})
export type OrderQuoteInput = z.infer<typeof OrderQuoteSchema>

/**
 * Edição de pedido pelo staff (garçom/dono). Itens, quando enviados,
 * SUBSTITUEM a lista atual. Só é possível antes do preparo começar
 * (pending/confirmed). Preço (#253): linha mantida conserva o preço e a
 * promoção do momento do pedido; linha trocada, com extras diferentes ou
 * quantidade alterada é re-precificada na hora da edição.
 *
 * `id` (opcional): o item gravado que a linha edita — pareamento exato. Sem
 * ele, o servidor pareia por assinatura (produto + variante + extras). Item
 * com `id` e sem `notes` mantém a observação gravada.
 */
export const OrderUpdateItemSchema = OrderItemSchema.extend({
  id: z.string().uuid().optional(),
})
export const OrderUpdateSchema = z.object({
  channel: z.enum(['dine_in', 'takeaway', 'delivery']).optional(),
  table_id: z.string().uuid().nullable().optional(),
  reference_name: z.string().max(64).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
  items: z.array(OrderUpdateItemSchema).min(1).max(50).optional(),
})
export type OrderUpdateInput = z.infer<typeof OrderUpdateSchema>

/**
 * Ajuste manual de preço pelo staff (#274) — POST /orders/:id/adjustments.
 *
 * - item: `price_override` (preço unitário manual, centavos, pode ser 0 ou
 *   acima do preço base), `discount_percent` (inteiro 1–100) ou
 *   `discount_amount` (centavos por unidade, ≤ preço base). Extras não entram
 *   (mesma regra das promoções).
 * - order (desconto no total do pedido): `discount_percent` (1–100) ou
 *   `discount_amount` (centavos, ≤ subtotal).
 * - `none` desfaz o ajuste do alvo (volta ao preço do pedido, sem
 *   re-precificar). Todo ajuste é auditado (quem/quando/antes/depois/motivo).
 */
export const PRICE_ADJUSTMENT_MAX_CENTS = 10_000_000
const AdjustmentReasonSchema = z.string().trim().max(200).nullable().optional()

function refineAdjustmentValue(
  v: { type: string; value?: number | undefined },
  ctx: z.RefinementCtx,
): void {
  if (v.type === 'none') {
    if (v.value !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: 'Sem valor para none' })
    }
    return
  }
  if (v.value === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: 'Informe o valor' })
    return
  }
  const [min, max] =
    v.type === 'discount_percent'
      ? [1, 100]
      : v.type === 'price_override'
        ? [0, PRICE_ADJUSTMENT_MAX_CENTS]
        : [1, PRICE_ADJUSTMENT_MAX_CENTS]
  if (v.value < min || v.value > max) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['value'],
      message: `Valor deve estar entre ${min} e ${max}`,
    })
  }
}

export const OrderItemAdjustmentSchema = z
  .object({
    target: z.literal('item'),
    order_item_id: z.string().uuid(),
    type: z.enum(['price_override', 'discount_percent', 'discount_amount', 'none']),
    value: z.number().int().optional(),
    reason: AdjustmentReasonSchema,
  })
  .strict()
export const OrderDiscountAdjustmentSchema = z
  .object({
    target: z.literal('order'),
    type: z.enum(['discount_percent', 'discount_amount', 'none']),
    value: z.number().int().optional(),
    reason: AdjustmentReasonSchema,
  })
  .strict()
export const OrderPriceAdjustmentSchema = z
  .discriminatedUnion('target', [OrderItemAdjustmentSchema, OrderDiscountAdjustmentSchema])
  .superRefine(refineAdjustmentValue)
export type OrderPriceAdjustmentInput = z.infer<typeof OrderPriceAdjustmentSchema>

/**
 * Pedido de ajuste (#274/#284): mesmo corpo do ajuste direto. O garçom só
 * pode PEDIR; Gerente/Dono aprovam (ou aplicam direto).
 */
export const OrderAdjustmentRequestSchema = OrderPriceAdjustmentSchema
export type OrderAdjustmentRequestInput = OrderPriceAdjustmentInput

/** Decisão de Gerente/Dono sobre um pedido de ajuste. Recusa pede motivo. */
export const AdjustmentDecisionSchema = z.object({
  note: z.string().trim().max(300).optional(),
})
export const AdjustmentRejectSchema = z.object({
  note: z.string().trim().min(1, 'Informe o motivo da recusa').max(300),
})

export type OrderCreateInput = z.infer<typeof OrderCreateSchema>
export type OrderItemInput = z.infer<typeof OrderItemSchema>

// ─── Restaurant Config ───────────────────────────────────────────────────────────

const HexColorSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Cor deve ser hex de 6 dígitos, ex.: #E53E3E')

const TimeSchema = z.string().regex(/^\d{2}:\d{2}$/, 'Horário deve estar no formato HH:MM')

// Validação ESTRUTURAL de locale (BCP-47 curto). A validação semântica — o
// locale existe no catálogo? — é feita em runtime pela API contra a tabela
// `locales` (adicionar idioma não exige mudança de código/schema).
// Aceita códigos legados com região ('pt-BR', 'en-US'…) na entrada e
// transforma para o código canónico sem região (#419).
const LocaleSchema = z
  .string()
  .min(2)
  .max(10)
  .regex(LOCALE_PATTERN, 'Locale deve ser BCP-47 (ex.: pt, en)')
  .transform(normalizeLocale)

// Fuso IANA validado contra o tzdata do runtime (Intl) — ver utils/timezone.ts
// e na forma canônica (America/sao_paulo e US/Eastern → 400 com sugestão).
export const TimezoneSchema = z
  .string()
  .max(64)
  .superRefine((tz, ctx) => {
    if (!isValidTimezone(tz)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: invalidTimezoneMessage(tz) })
    }
  })

/**
 * #161: 'online' = a parte é paga pela plataforma (Stripe Connect, direct
 * charge na conta conectada do restaurante). Os métodos presenciais seguem
 * iguais; o pagamento online só abre quando a conta conectada está pronta
 * (a rota /bills/:id/pay-online valida o estado real, não só a config).
 */
export const PaymentMethodSchema = z.enum(['cash', 'card_local', 'online'])

/**
 * #171: moeda do restaurante (restaurant_config.currency, migração 0136) —
 * só o FORMATO aqui (ISO 4217, 3 maiúsculas); a lista de moedas suportadas
 * é validada na rota com isSupportedCurrency (locale/country-defaults).
 */
export const CurrencyCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Moeda inválida (ISO 4217).')

/**
 * #486 item 1: país do restaurante (tenants.country_code, NOT NULL) — aqui só
 * normaliza (trim + maiúsculas); o FORMATO ISO 3166-1 alpha-2 e a lista
 * completa de países (ALL_COUNTRY_CODES, 249 + XK) são validados na rota com
 * 422 COUNTRY_INVALID — mesmo contrato do currency (422 CURRENCY_INVALID).
 */
export const CountryCodeSchema = z.string().trim().toUpperCase()

export const RestaurantConfigUpdateSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  about: z.string().max(1000).nullable().optional(),
  primary_color: HexColorSchema.optional(),
  secondary_color: HexColorSchema.optional(),
  background_color: HexColorSchema.optional(),
  header_color: HexColorSchema.optional(),
  button_active_color: HexColorSchema.optional(),
  logo_bg_color: HexColorSchema.nullable().optional(),
  /**
   * Template do cardápio do cliente (migração 0084, proposta menu-feed):
   * 'list' = lista clássica; 'grid' = grade 2× com filtros. Default 'list'.
   */
  menu_template: z.enum(['list', 'grid', 'feed_1']).optional(),
  /**
   * Fluxo dos pedidos da mesa (migração 0085, F1c): 'full' = pipeline
   * completo (cozinha + salão); 'waiter' = modo garçom — o pedido termina no
   * garçom, que o lança no sistema local do restaurante. Default 'full'.
   */
  order_flow: z.enum(['full', 'waiter']).optional(),
  primary_locale: LocaleSchema.optional(),
  target_locales: z.array(LocaleSchema).max(32).optional(),
  is_open: z.boolean().optional(),
  /**
   * Modo de abertura (#332, migração 0080): 'auto' segue o interruptor
   * (is_open) + o horário de funcionamento; 'open'/'closed' são overrides do
   * dono que vencem os dois. No PUT, o modo sincroniza is_open (open → true,
   * closed → false, auto → true).
   */
  open_mode: z.enum(['auto', 'open', 'closed']).optional(),
  allow_takeaway: z.boolean().optional(),
  allow_delivery: z.boolean().optional(),
  tip_options: z.array(z.number().int().min(0).max(100)).max(5).optional(),
  tip_mode: z.enum(['optional', 'suggested', 'none']).optional(),
  payment_methods: z.array(PaymentMethodSchema).min(1).max(4).optional(),
  timezone: TimezoneSchema.optional(),
  /**
   * #171 (0136): moeda do restaurante — a do país por omissão (mapa
   * país → moeda), editável pelo dono. Mudar NÃO converte preços: os
   * produtos guardam a moeda com que nasceram (a rota avisa).
   */
  currency: CurrencyCodeSchema.optional(),
  /**
   * #486 item 1: país do restaurante (ISO 3166-1 alpha-2; 422 fora do mapa).
   * Mudar NÃO converte preços nem toca em primary_locale — só país+moeda.
   */
  country_code: CountryCodeSchema.optional(),
  /**
   * #129 (0099): pedir NIF/CPF no checkout do cardápio — 'off' não pede,
   * 'optional' campo livre, 'required' obrigatório para o cliente.
   */
  fiscal_id_mode: z.enum(['off', 'optional', 'required']).optional(),
  /** #133 (0100): SLA de espera do KDS em minutos (1–240; default 15) */
  sla_minutes: z.number().int().min(1).max(240).optional(),
  /**
   * Limite do garçom (#274/#284, 0056): ajuste de preço (desconto OU
   * acréscimo) até este limite aplica na hora; acima — ou sem limite
   * configurado — vira pedido para Gerente/Dono. `percent` = % inteiro
   * 0–100 sobre o preço de lista; `amount` = centavos por ajuste.
   * type null = sem limite.
   */
  waiter_adjust_limit_type: z.enum(['percent', 'amount']).nullable().optional(),
  waiter_adjust_limit_value: z.number().int().min(0).max(100_000_000).nullable().optional(),
})

/**
 * Hora do dia no PUT de horário: '' (ou só espaços) = hora AUSENTE (null),
 * como o painel já faz. Notas do QA: `close_time: ""` direto na API dava o
 * 400 genérico de formato em vez de INCOMPLETE_HOURS com os dias em causa.
 */
const HourOrEmptySchema = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
  TimeSchema.nullable(),
)

export const OperatingHoursUpdateSchema = z.object({
  hours: z
    .array(
      z.object({
        day_of_week: z.number().int().min(0).max(6),
        open_time: HourOrEmptySchema,
        close_time: HourOrEmptySchema,
      }),
    )
    .length(7, 'Enviar todos os 7 dias da semana'),
})

// ─── Bills (fechar a conta) ──────────────────────────────────────────────────

/**
 * Abertura da conta da mesa. 'single'/'by_people' já nascem enviadas ao
 * garçom (awaiting_payment); 'flexible' abre a sessão de divisão.
 * payment_methods: 1 método p/ single, X métodos (um por pessoa) p/
 * by_people; flexível define método por participante no claim.
 */
export const BillCreateSchema = z
  .object({
    table_id: z.string().uuid(),
    mode: z.enum(['single', 'by_people', 'flexible']),
    people_count: z.number().int().min(1).max(20).optional(),
    payment_methods: z.array(PaymentMethodSchema).max(20).optional(),
    tip_cents: z.number().int().min(0).default(0),
  })
  .refine((b) => b.mode !== 'by_people' || (b.people_count ?? 0) >= 1, {
    message: 'by_people exige people_count',
    path: ['people_count'],
  })
  .refine((b) => b.mode !== 'single' || (b.payment_methods?.length ?? 0) === 1, {
    message: 'single exige exatamente 1 forma de pagamento',
    path: ['payment_methods'],
  })
  .refine((b) => b.mode !== 'by_people' || (b.payment_methods?.length ?? 0) === b.people_count, {
    message: 'by_people exige 1 forma de pagamento por pessoa',
    path: ['payment_methods'],
  })

/**
 * Parte de um participante na divisão flexível. União discriminada:
 * por itens (quantidades marcadas + dividir entre N) ou valor fixo.
 */
export const BillClaimSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('items'),
    items: z
      .array(
        z.object({
          order_item_id: z.string().uuid(),
          quantity: z.number().int().min(1).max(20),
        }),
      )
      .min(1)
      .max(50),
    divide_by: z.number().int().min(1).max(10).default(1),
    payment_method: PaymentMethodSchema,
    display_name: z.string().max(64).optional(),
  }),
  z.object({
    kind: z.literal('fixed'),
    amount_cents: z.number().int().min(1),
    payment_method: PaymentMethodSchema,
    display_name: z.string().max(64).optional(),
  }),
])

export type BillCreateInput = z.infer<typeof BillCreateSchema>
export type BillClaimInput = z.infer<typeof BillClaimSchema>
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>

/**
 * Pagamento online de uma parte da conta (#161): o valor é SEMPRE calculado
 * no servidor (parte do participante / próxima cota / restante do single);
 * o corpo só carrega a gorjeta extra escolhida e a URL de retorno do
 * Checkout (página QR do cardápio, origem de primeira parte).
 */
export const BillPayOnlineSchema = z.object({
  /** gorjeta EXTRA além da já incluída no total da conta */
  tip_cents: z.number().int().min(0).default(0),
  /** página do QR de onde o pagamento partiu — o Checkout volta para ela */
  return_url: z.string().url().max(2048),
  /** divisão flexível: parte do participante a pagar (padrão: a do viewer) */
  participant_id: z.string().uuid().optional(),
})

export type BillPayOnlineInput = z.infer<typeof BillPayOnlineSchema>

/** Payload de resposta do POST /bills/:id/pay-online. */
export const BillPayOnlineResultSchema = z.object({
  payment_id: z.string().uuid(),
  checkout_url: z.string().url(),
  amount_cents: z.number().int().min(1),
  tip_cents: z.number().int(),
  currency: z.string().length(3),
})

export type BillPayOnlineResult = z.infer<typeof BillPayOnlineResultSchema>

// ─── Categorias ──────────────────────────────────────────────────────────────────

export const CategoryCreateSchema = z.object({
  name: z.string().min(1).max(80),
  locale: LocaleSchema.default('pt'),
  sort_order: z.number().int().min(0).default(0),
  is_active: z.boolean().default(true),
})

export const CategoryUpdateSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  locale: LocaleSchema.optional(),
  sort_order: z.number().int().min(0).optional(),
  is_active: z.boolean().optional(),
})

export const CategoryTranslationUpsertSchema = z.object({
  locale: LocaleSchema,
  name: z.string().min(1).max(80),
})

// ─── Produtos ────────────────────────────────────────────────────────────────────

const ProductChannelsSchema = z.object({
  available_dine_in: z.boolean().default(true),
  available_takeaway: z.boolean().default(true),
  available_delivery: z.boolean().default(true),
})

export const ProductCreateSchema = z
  .object({
    category_id: z.string().uuid(),
    sku: z.string().max(64).optional(),
    price_cents: z.number().int().min(0),
    currency: z.string().length(3).default('BRL'),
    tax_category: z.enum(['food', 'beverage', 'tobacco', 'other']).default('food'),
    is_available: z.boolean().default(true),
    sort_order: z.number().int().min(0).default(0),
    name: z.string().min(1).max(120),
    description: z.string().max(600).optional(),
    /** #403: o painel manda o CÓDIGO BASE do idioma — sem criar locale regional */
    locale: LocaleSchema.optional(),
  })
  .merge(ProductChannelsSchema)

export const ProductUpdateSchema = z.object({
  category_id: z.string().uuid().optional(),
  sku: z.string().max(64).nullable().optional(),
  price_cents: z.number().int().min(0).optional(),
  currency: z.string().length(3).optional(),
  tax_category: z.enum(['food', 'beverage', 'tobacco', 'other']).optional(),
  is_available: z.boolean().optional(),
  is_active: z.boolean().optional(),
  available_dine_in: z.boolean().optional(),
  available_takeaway: z.boolean().optional(),
  available_delivery: z.boolean().optional(),
  sort_order: z.number().int().min(0).optional(),
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(600).nullable().optional(),
  /** #387: linha de tradução a gravar — o painel manda o locale da linha lida
   * (em vez de o servidor assumir o primário e criar linha nova). */
  locale: LocaleSchema.optional(),
  variant_group_label: z.string().max(60).nullable().optional(),
  /** cozinha (estação de produção) que prepara este produto */
  kitchen_id: z.string().uuid().nullable().optional(),
})

export const ProductTranslationUpsertSchema = z.object({
  locale: LocaleSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(600).nullable().optional(),
  variant_group_label: z.string().max(80).nullable().optional(),
})

/**
 * #325: o dono confirma a lista de alergénios de um produto — códigos 1–14
 * do Reg. UE 1169/2011, Anexo II. Lista vazia = "sem alergénios".
 */
export const ProductAllergensConfirmSchema = z.object({
  allergens: z
    .array(z.number().int().min(1).max(14))
    .max(14)
    .refine((codes) => new Set(codes).size === codes.length, 'Alergénio repetido.'),
})

/**
 * #214: o dono confirma os atributos de um produto (dietas, picância, tags
 * de sabor, infantil, prato da casa). O corpo é o estado FINAL completo —
 * igual ao endpoint de alergénios, o que não vem é descartado. Todos os
 * campos são opcionais para poder confirmar "sem nenhum atributo" com {}.
 */
export const ProductAttributesConfirmSchema = z.object({
  diet_tags: z
    .array(z.enum(DIET_TAGS))
    .max(DIET_TAGS.length)
    .refine((tags) => new Set(tags).size === tags.length, 'Dieta repetida.')
    .optional(),
  spice_level: z.number().int().min(0).max(3).optional(),
  flavor_tags: z
    .array(
      z
        .string()
        .trim()
        .toLowerCase()
        .min(2)
        .max(24)
        .regex(/^[\p{L}\p{N}][\p{L}\p{N} -]*$/u, 'Tag de sabor com caracteres inválidos.'),
    )
    .max(MAX_FLAVOR_TAGS)
    .refine((tags) => new Set(tags).size === tags.length, 'Tag de sabor repetida.')
    .optional(),
  is_kids: z.boolean().optional(),
  is_signature: z.boolean().optional(),
})

export const IngredientCreateSchema = z.object({
  name: z.string().min(1).max(80),
  is_removable: z.boolean().default(true),
  sort_order: z.number().int().min(0).default(0),
})

export const IngredientUpdateSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  is_removable: z.boolean().optional(),
  sort_order: z.number().int().min(0).optional(),
})

export const ExtraCreateSchema = z.object({
  name: z.string().min(1).max(80),
  price_type: z.enum(['fixed', 'percent']).default('fixed'),
  price_value: z.number().int().min(0).max(1_000_000),
  is_active: z.boolean().default(true),
})

export const ExtraUpdateSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  price_type: z.enum(['fixed', 'percent']).optional(),
  price_value: z.number().int().min(0).max(1_000_000).optional(),
  is_active: z.boolean().optional(),
})

export const VariantCreateSchema = z.object({
  name: z.string().min(1).max(80),
  price_cents: z.number().int().min(0),
  sort_order: z.number().int().min(0).default(0),
})
export const VariantUpdateSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  price_cents: z.number().int().min(0).optional(),
  sort_order: z.number().int().min(0).optional(),
  is_available: z.boolean().optional(),
})

// ─── Combos ──────────────────────────────────────────────────────────────────────

export const ComboCreateSchema = z.object({
  name: z.string().min(1).max(100),
  discount_type: z.enum(['fixed', 'percent']).default('percent'),
  discount_value: z.number().int().min(0),
  is_active: z.boolean().default(true),
  starts_at: z.number().int().positive().nullable().optional(),
  ends_at: z.number().int().positive().nullable().optional(),
  items: z
    .array(
      z.object({
        product_id: z.string().uuid(),
        quantity: z.number().int().min(1).max(99),
      }),
    )
    .min(2, 'Combo deve ter pelo menos 2 itens'),
})

export const ComboUpdateSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  discount_type: z.enum(['fixed', 'percent']).optional(),
  discount_value: z.number().int().min(0).optional(),
  is_active: z.boolean().optional(),
  starts_at: z.number().int().positive().nullable().optional(),
  ends_at: z.number().int().positive().nullable().optional(),
  items: z
    .array(
      z.object({
        product_id: z.string().uuid(),
        quantity: z.number().int().min(1).max(99),
      }),
    )
    .min(2)
    .optional(),
})

// ─── Tax Rules ───────────────────────────────────────────────────────────────────

export const TaxRuleCreateSchema = z.object({
  tax_category: z.enum(['food', 'beverage', 'tobacco', 'other']),
  rate_percent: z.number().int().min(0).max(10000), // base centesimal: 1300 = 13%
  label: z.string().min(1).max(50),
  is_inclusive: z.boolean().default(true),
})

export const TaxRuleUpdateSchema = z.object({
  rate_percent: z.number().int().min(0).max(10000).optional(),
  label: z.string().min(1).max(50).optional(),
  is_inclusive: z.boolean().optional(),
  is_active: z.boolean().optional(),
})

// ─── Mesas ───────────────────────────────────────────────────────────────────────

export const TableCreateSchema = z.object({
  number: z.number().int().min(1).max(9999),
  label: z.string().max(50).optional(),
})

export const TableUpdateSchema = z.object({
  label: z.string().max(50).nullable().optional(),
  is_active: z.boolean().optional(),
})

export const TablesGenerateSchema = z.object({
  count: z.number().int().min(1).max(200),
  start_from: z.number().int().min(1).default(1),
})

// ─── Mesas — garçom avançado (#132) ───────────────────────────────────────────

/**
 * Unir mesa A (rota) na mesa B (target): pedidos em andamento, conta ativa,
 * chamadas abertas e claims migram para B; A fica inativa apontando para B.
 */
export const TableMergeSchema = z.object({
  target_table_id: z.string().uuid(),
})

/**
 * Garçom declara a mesa que atende. Corpo vazio = o próprio usuário;
 * Gerente/Dono podem atribuir a outro membro da equipe.
 */
export const TableClaimSchema = z.object({
  waiter_user_id: z.string().uuid().optional(),
})

// ─── Cozinhas (estações de produção) ────────────────────────────────────────────

export const KitchenCreateSchema = z.object({
  name: z.string().min(1).max(60),
  sort_order: z.number().int().min(0).optional(),
})

export const KitchenUpdateSchema = z.object({
  name: z.string().min(1).max(60).optional(),
  sort_order: z.number().int().min(0).optional(),
  active: z.boolean().optional(),
})

/** Direciona TODOS os produtos de uma categoria para uma cozinha (null = principal). */
export const KitchenAssignCategorySchema = z.object({
  category_id: z.string().uuid(),
  kitchen_id: z.string().uuid().nullable(),
})

// ─── Pagamento ───────────────────────────────────────────────────────────────────

// ─── Platform ─────────────────────────────────────────────────────────────────

export const TenantCreateSchema = z.object({
  // #418: bound de segurança apenas (200) — formato/comprimento válidos são
  // responsabilidade do checkSlug na rota, que responde 422 com reason +
  // suggestion em vez de um 400 genérico do Zod
  slug: z.string().trim().min(1).max(200),
  name: z.string().min(2).max(100),
  country_code: z.string().length(2),
  locale: LocaleSchema.default('pt'),
  owner_email: EmailSchema.optional(),
})

export const TenantUpdateSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  status: z.enum(['active', 'inactive', 'deleted']).optional(),
  /** país do restaurante — só o suporte (platform_owner) muda (#293) */
  country_code: z
    .string()
    .regex(/^[A-Za-z]{2}$/)
    .optional(),
})

/**
 * Convite de usuário de TENANT (restaurante). platform_owner é criado apenas
 * no tenant da plataforma via POST /platform/users — aceitar essa role num
 * convite de restaurante criaria super-usuário cross-tenant silencioso
 * (a role ignora a checagem de tenant do middleware authenticate).
 */
export const UserInviteSchema = z
  .object({
    email: EmailSchema,
    /** Lista de roles de staff (compat: `role` único também é aceito). */
    roles: z
      .array(z.enum(['restaurant_owner', 'manager', 'kitchen', 'waiter']))
      .min(1)
      .max(4)
      .optional(),
    role: z.enum(['restaurant_owner', 'manager', 'kitchen', 'waiter']).optional(),
    display_name: z.string().max(100).optional(),
  })
  .refine((v) => v.roles !== undefined || v.role !== undefined, {
    message: 'Informe role ou roles',
  })

// ─── Pagamento ────────────────────────────────────────────────────────────────

export const PaymentCreateSchema = z.object({
  order_id: z.string().uuid(),
  gateway_slug: z
    .enum([
      'stripe',
      'pix',
      'pag_seguro',
      'mb_way',
      'multibanco',
      'ideal',
      'swish',
      'vipps',
      'razorpay',
    ])
    .optional(),
  split_type: z.enum(['percent', 'fixed', 'by_items']).optional(),
  split_value: z.number().int().min(0).optional(),
  tip_cents: z.number().int().min(0).default(0),
  paid_item_ids: z.array(z.string().uuid()).optional(),
})
